import React, {useContext, useEffect, useState} from "react";
import {translateService} from "../../services/TranslateService";
import styles from "./SearchFilter.module.scss";
import {cx} from "../MakeReservation/util";
import Coupon from "../MakeReservation/Coupon";
import {DataContext} from "../../contexts/dataContext";
import useFilter, {
    Country,
    FilterParameters,
    FuelType,
    Transmission,
    NonCategorisedOptions,
    VehicleType, TyreTypes, ResultsTypes
} from "./Filter";
import Modal from "react-modal";
import {cStylesFilters} from "../../CustomModalStyles";


// Filter options:
const fuelOptions = ['all_fuel', 'e95', 'diesel', 'hybrid', 'electric'];
const countryOptions = ['all_finland', 'scandinavia', 'baltics', 'europe'];
const vehicleTypeOptions = ['all_vehicleType', 'manual', 'automatic'];
const tyreTypeOptions = ['all_tyreTypes', 'allSeasonTyres', 'studdedTyres', 'summerTyres'];


export default function SearchFilter({openFilters, setCloseFilters}:
        { openFilters: boolean; setCloseFilters: Function }) {
    const { rootUrl, changeSearchParameters, filteredParams, filteredVehicles, mapData, searchParameters,
        acrissCodeFromUrl, changeAcrissCodeFromUrl, modelId, changeModelId, showingModelsResults, changeShowingModelsResults } = useContext(DataContext);
    const { handleFilters } = useFilter();

    // Filter states — initialize from existing filteredParams so a second instance (e.g. map fullscreen) inherits the current selections
    const [selectedFuelType, setSelectedFuelType] = useState<string>(filteredParams.fuelType || 'all_fuel');
    const [selectedCountry, setSelectedCountry] = useState<string>(filteredParams.selectedTag || 'all_finland');
    const [selectedVehicleType, setSelectedVehicleType] = useState<string[]>(acrissCodeFromUrl ? [acrissCodeFromUrl] : (filteredParams.types || []));
    const [selectedTyreType, setSelectedTyreType] = useState<string>(filteredParams.tyreTypesNew || 'all_tyreTypes');
    const [selectTransmission, setSelectTransmission] = useState<string>(filteredParams.transmission || 'all_vehicleType');
    const [selectedOtherTypes, setSelectedOtherTypes] = useState<string[]>(filteredParams.otherTypes || []);
    const [selectedModelId, setSelectedModelId] = useState<number[] | undefined>(modelId);

    const ResetFilters = () => {
        handleRemoveModelSearch();
        setSelectedFuelType('all_fuel');
        setSelectedCountry('all_finland');
        setSelectedVehicleType([]);
        setSelectedTyreType('all_tyreTypes');
        setSelectTransmission('all_vehicleType');
        setSelectedOtherTypes([]);
        setSelectedModelId(undefined);
        changeShowingModelsResults(false);
        sessionStorage.setItem('filterChange', '1');
        if (modelId) {
            changeModelId(undefined);
        }
    }

    useEffect(() => {
        if(filteredParams.reset) {
            ResetFilters();
        }
    },[filteredParams, filteredVehicles]);

    useEffect(() => {
        if (acrissCodeFromUrl) {
            setSelectedVehicleType([acrissCodeFromUrl]);
            changeAcrissCodeFromUrl(undefined);
        }
    }, [changeAcrissCodeFromUrl, acrissCodeFromUrl])

    // Reflect the car/van segment chosen at search in the body-type filter: van => van only;
    // car => all car body types (compact/stw/suv/minibus). Runs on mount (the filter mounts
    // once results exist) and whenever the segment changes.
    useEffect(() => {
        const seg = searchParameters?.vehicleSegment;
        if (seg === 'van') { setSelectedVehicleType(['van']); }
        else if (seg === 'car') { setSelectedVehicleType(['compact', 'stw', 'suv', 'minibus']); }
    }, [searchParameters?.vehicleSegment])

    useEffect(() => {
        if (modelId) {
            setSelectedModelId(modelId);
            if (!showingModelsResults) {
                changeShowingModelsResults(true);
            }
        } else {
            setSelectedModelId(undefined);
        }
    }, [modelId, showingModelsResults])

    useEffect(() => {
        // when the filter options are selected, it will call the handleFilters function to filter the vehicles
        if(selectedFuelType && selectedCountry && selectedVehicleType && selectedTyreType && selectTransmission &&
            selectedOtherTypes){
            const filterParameters: FilterParameters = {
                types: selectedVehicleType as VehicleType[],
                fuelType: selectedFuelType as FuelType,
                selectedTag: selectedCountry as Country,
                tyreTypesNew: selectedTyreType as TyreTypes,
                transmission: selectTransmission as Transmission,
                otherTypes: selectedOtherTypes as NonCategorisedOptions[],
                model: showingModelsResults ? modelId : undefined,
                resultsType: showingModelsResults ? ResultsTypes.modelsOnly : ResultsTypes.All,
                reset: false
            };
            handleFilters(filterParameters);
        }
        // do not add 'handleFilters' to the dependency array, as it will cause an infinite loop
    }, [selectedFuelType, selectedCountry, selectedVehicleType, selectedTyreType, selectTransmission, selectedOtherTypes,
        acrissCodeFromUrl, selectedModelId, modelId, showingModelsResults, mapData]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const vehicleType = name.toLowerCase() as VehicleType;
        sessionStorage.setItem('filterChange', '1');
        if (event.target.checked && !selectedVehicleType.includes(vehicleType)) {
            setSelectedVehicleType((prev: string[]) => [...prev, vehicleType]);
        } else {
            setSelectedVehicleType((prev: string[]) => prev.filter(type => type !== vehicleType));
        }
    };

    const activeFilterCount = [
        selectedVehicleType.length > 0,
        selectedFuelType !== 'all_fuel',
        selectTransmission !== 'all_vehicleType',
        selectedTyreType !== 'all_tyreTypes',
        selectedCountry !== 'all_finland',
        selectedOtherTypes.length > 0,
    ].filter(Boolean).length;

    const handleRemoveModelSearch = () => {
        sessionStorage.setItem('sortWait', '1');
        setSelectedModelId(undefined);
        changeModelId(undefined);
        sessionStorage.setItem('filterChange', '1');
    }


    const PillGroup = ({options, selected, onChange, joined}: {options: string[], selected: string, onChange: (value: string) => void, joined?: boolean}) => {
        const colsClass = options.length === 4 ? styles.pillGroupCols2 : styles.pillGroupCols3;
        return (
            <div className={joined ? styles.pillGroupJoined : `${styles.pillGroup} ${colsClass}`}>
                {options.map(option => (
                    <button key={option} type="button"
                            className={`${styles.pill} ${selected === option ? styles.pillActive : ''} ${joined ? styles.pillJoined : ''}`}
                            onClick={() => onChange(option)}>
                        {translateService.translate('vehicleSearch.' + option.split('_')[0])}
                    </button>
                ))}
            </div>
        );
    }

    const PillToggle = ({name}: {name: string}) => {
        const isActive = selectedOtherTypes.includes(name);
        const toggle = () => {
            sessionStorage.setItem('filterChange', '1');
            if (isActive) {
                setSelectedOtherTypes(prev => prev.filter(t => t !== name));
            } else {
                setSelectedOtherTypes(prev => [...prev, name]);
            }
        };
        return (
            <button type="button"
                    className={`${styles.pill} ${isActive ? styles.pillActive : ''}`}
                    onClick={toggle}>
                {translateService.translate(`vehicleSearch.${name}`)}
            </button>
        );
    }

    const CarTypeCheckbox = ({name, style, icon, onChange, className}: {name: string, style?: React.CSSProperties, icon?: string, onChange?: React.ChangeEventHandler<HTMLInputElement>, className?: string}) => {
        const isSelected = selectedVehicleType?.includes(name);
        return <>
            <label className={cx(styles.carTypeCheckbox, isSelected ? styles.carTypeSelected : styles.carTypeUnselected)}
                   htmlFor={name}>
                <input type="checkbox"
                       checked={selectedVehicleType.includes(name)}
                       value={name}
                       id={name}
                       name={name}
                       onChange={onChange}
                       className={styles.carTypeHiddenCheckbox}
                />
                <div className={styles.r24SelectCarModelImage}>
                    {icon === 'parking' ?
                        <img width="14" height="17" style={style}
                             src={rootUrl + "/assets/icons/new/search/icon-model-select-" + icon + ".png"}
                             alt="icon-model-select-" loading="lazy"/>
                        :<img width="30" height="17" style={style}
                              src={rootUrl + "/assets/icons/new/search/icon-model-select-" + icon + ".svg"}
                              alt="icon-model-select-" loading="lazy"/>}

                </div>
                <div className={styles.r24SelectCarModel}>
                    {translateService.translate('vehicleSearch.' + name)}
                </div>
            </label>
        </>
    }


    return (
        <>
            { modelId?.length && showingModelsResults ?
                <div className='mt-2'>
                    <div className={styles.searchFilter}>
                        <div className={styles.modelResultsContainer}>
                            <span className={styles.r24filterResults}> {translateService.translate('vehicleSearch.showingModelBasedResults')} </span>
                            <div className={styles.r24HalfWidthFlexEnd}>
                                <button id={'removeModelFilterBtn'}
                                        onClick={() => handleRemoveModelSearch()}
                                        className="btn btn-info btn-outline-info btn-all w-100 font-weight-bold">
                                    {translateService.translate('vehicleSearch.removedModelFilterBtn')}
                                </button>
                            </div>
                    </div>
                </div>
            </div> : null }
            <Modal isOpen={openFilters} style={Object.assign(cStylesFilters)}
                   portalClassName={'filterModalPortal'}>
                <div className={styles.searchFilter}>
                    <div className={cx(styles.searchFilter, styles.noBorderAround, styles.r24FullWidth, styles.r24BottomMargin10)} id="services">
                        <div >
                            <button className={cx('btn btn-sm btn-primary btn-all', styles.topEscapeBtn)}
                                    onClick={() => {setCloseFilters(false)}}> X </button>
                            <div className={cx(styles.r24FullWidthFlexCenter)} >
                                <span className={styles.r24filterResults}>
                                    {translateService.translate('vehicleSearch.filterResults')}
                                </span>
                            </div>
                        </div>
                        { selectedModelId ?
                            <div className={styles.modelResultsContainer}>
                                <span className={styles.r24filterResults}> {translateService.translate('vehicleSearch.showingModelBasedResults')} </span>
                                <div className={styles.r24HalfWidthFlexEnd}>
                                    <button id={'removeModelFilterBtn'}
                                            onClick={() => handleRemoveModelSearch()}
                                            className="btn btn-info btn-outline-info btn-all w-100 font-weight-bold">
                                        {translateService.translate(showingModelsResults ? 'vehicleSearch.removedModelFilterBtn' : 'vehicleSearch.backToAllVehicles')}
                                    </button>
                                </div>
                                <br/>
                            </div>
                            : <>
                                <div className={cx(styles.r24FullWidth, styles.dottedGrayline, styles.r24TopBottomMargin5)}></div>
                                <div className={styles.r24FullWidthFlexEnd} style={{padding: '10px 0 0 0'}}>
                                    <CarTypeCheckbox name={"compact"} className={styles.carTypeCheckboxComponent} icon={"medium"} onChange={handleInputChange} />
                                    <CarTypeCheckbox name={"stw"} className={styles.carTypeCheckboxComponent} icon={"large"} onChange={handleInputChange}/>
                                    <CarTypeCheckbox name={"suv"} className={styles.carTypeCheckboxComponent} icon={"suv"} onChange={handleInputChange} />
                                    <CarTypeCheckbox name={"minibus"} className={styles.carTypeCheckboxComponent} icon={"minibus"} onChange={handleInputChange}/>
                                    <CarTypeCheckbox name={"van"} className={styles.carTypeCheckboxComponent} icon={"van"} onChange={handleInputChange}/>
                                </div>
                                <div className={cx(styles.r24FullWidth, styles.dottedGrayline, styles.r24TopBottomMargin5)}></div>
                            </> }
                        <div className={styles.filterSetsBody}>
                            <div className={styles.filterSection}>
                                <span className={styles.filterSectionLabel}>{translateService.translate('vehicleSearch.transmission')}</span>
                                <PillGroup options={vehicleTypeOptions} selected={selectTransmission}
                                           onChange={(v) => setSelectTransmission(v)} />
                            </div>
                            <div className={styles.filterSection}>
                                <span className={styles.filterSectionLabel}>{translateService.translate('vehicleSearch.tyres')}</span>
                                <PillGroup options={tyreTypeOptions} selected={selectedTyreType}
                                           onChange={(v) => setSelectedTyreType(v)} />
                            </div>
                            <div className={styles.filterSection}>
                                <span className={styles.filterSectionLabel}>{translateService.translate('vehicleSearch.fuelType')}</span>
                                <PillGroup options={fuelOptions} selected={selectedFuelType}
                                           onChange={(v) => setSelectedFuelType(v)} />
                            </div>
                            <div className={styles.filterSection}>
                                <span className={styles.filterSectionLabel}>{translateService.translate('vehicleSearch.crossBorderTravel')}</span>
                                <PillGroup options={countryOptions} selected={selectedCountry}
                                           onChange={(v) => setSelectedCountry(v)} />
                            </div>
                            <div className={styles.filterSection}>
                                <span className={styles.filterSectionLabel}>{translateService.translate('vehicleSearch.equipment')}</span>
                                <div className={`${styles.pillGroup} ${styles.pillGroupCols2}`}>
                                    <PillToggle name={'parkinghall'} />
                                    <PillToggle name={'parkingPermit'} />
                                    <PillToggle name={'towingHitch'} />
                                    <PillToggle name={'eco'} />
                                </div>
                            </div>
                            <Coupon changeSearchParameters={changeSearchParameters}/>
                        </div>
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.r24TopBottomMargin5, styles.filterCloseButtons)}>
                            <button className={'btn btn-info btn-outline-info btn-all w-50 font-weight-bold'}
                                    onClick={() => {ResetFilters(); setCloseFilters(false)}}>
                                {translateService.translate('vehicleSearch.clearFilters')}
                            </button>
                            <button className={'btn btn-primary btn-outline-primary btn-all w-50 font-weight-bold mr-3'}
                                    onClick={() => {setCloseFilters(false)}}>
                                {translateService.translate('vehicleSearch.useSelectedFilters')} ({activeFilterCount})
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}